import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';

export const DEFAULT_PER_PAGE = 80;
const pageSizes = [10, 20, 40, 80];

type Props = {
  className?: string;
  totalPages: number;
  page: number;
  totalItems?: number;
  perPage?: number;
  onChangePerPage?: (count: number) => void | null;
  onChangePage: (selectedItem: { selected: number }) => void;
};

const Pagination = ({
  className,
  totalPages,
  page,
  totalItems,
  perPage = DEFAULT_PER_PAGE,
  onChangePage,
  onChangePerPage,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false);

  const currentPage = page + 1;

  const { startItem, endItem } = useMemo(() => {
    if (!totalItems) return { startItem: 0, endItem: 0 };
    return {
      startItem: (currentPage - 1) * perPage + 1,
      endItem: Math.min(currentPage * perPage, totalItems),
    };
  }, [totalItems, perPage, currentPage]);

  const handlePrevious = () => currentPage > 1 && onChangePage({ selected: page - 1 });

  const handleNext = () => currentPage < totalPages && onChangePage({ selected: page + 1 });

  const buttonBase = 'p-[4px] rounded text-white/60 transition';
  const buttonEnabled = 'cursor-pointer hover:bg-gray-700';
  const buttonDisabled = 'opacity-50 cursor-not-allowed';

  return (
    <div className={clsx('flex items-center gap-3 ml-auto', className)}>
      {typeof perPage === 'number' && onChangePerPage && (
        <div className="flex items-center gap-[8px]">
          <span className="text-sm text-white/60">Строк на странице:</span>
          <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
              <div className="flex items-center gap-1 cursor-pointer">
                <span className="text-sm text-white">{perPage}</span>
                {open ? (
                  <ChevronUp size={24} className="text-white/60" />
                ) : (
                  <ChevronDown size={24} className="text-white/60" />
                )}
              </div>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="bg-gray-800 text-white rounded-md py-2 px-[10px] z-30 outline-none"
                align="start"
                sideOffset={8}
              >
                {pageSizes.map(size => (
                  <div
                    key={size}
                    className="cursor-pointer px-3 py-2 rounded hover:bg-gray-600"
                    onClick={() => onChangePerPage(size)}
                  >
                    {size}
                  </div>
                ))}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
      )}
      {typeof totalItems === 'number' && (
        <span className="text-sm text-white">
          {startItem}-{endItem} из {totalItems}
        </span>
      )}
      <div className="flex items-center gap-[4px]">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={`${buttonBase} ${currentPage === 1 ? buttonDisabled : buttonEnabled}`}
        >
          <ChevronLeft size={24} className="text-white/60" />
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={`${buttonBase} ${currentPage === totalPages ? buttonDisabled : buttonEnabled}`}
        >
          <ChevronRight size={24} className="text-white/60" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
